import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ValuationType = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('valuation_types');
  const [viewItem, setViewItem] = useState(null);
  const [valuationTypes, setValuationTypes] = useState([]);
  const [accountCategoryReferences, setAccountCategoryReferences] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [selectedAccountCategoryRef, setSelectedAccountCategoryRef] = useState('');
  const [externalInternalPOsAllowed, setExternalInternalPOsAllowed] = useState(false);

  useEffect(() => {
    loadValuationTypes();
    loadAccountCategoryReferences();
  }, []);

  const loadValuationTypes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/valuation-types`);
      setValuationTypes(response.data || []);
    } catch (error) {
      console.error('Error loading valuation types:', error);
      toast.error('Failed to load valuation types');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAccountCategoryReferences = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/account-category-references`);
      setAccountCategoryReferences(response.data || []);
    } catch (error) {
      console.error('Error loading account category references:', error);
    }
  };

  const handleOpenDialog = (type = null) => {
    setEditingType(type);
    if (type) {
      setSelectedAccountCategoryRef(type.account_category_reference_id);
      setExternalInternalPOsAllowed(type.external_internal_pos_allowed);
    } else {
      setSelectedAccountCategoryRef('');
      setExternalInternalPOsAllowed(false);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      valuation_type_id: (formData.get('valuation_type_id') || '').toUpperCase(),
      account_category_reference_id: selectedAccountCategoryRef,
      external_internal_pos_allowed: externalInternalPOsAllowed
    };

    try {
      if (editingType) {
        await axios.put(`${BACKEND_URL}/api/valuation-types/${editingType.valuation_type_id}`, {
          account_category_reference_id: data.account_category_reference_id,
          external_internal_pos_allowed: data.external_internal_pos_allowed
        });
        toast.success('Valuation Type updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/valuation-types`, data);
        toast.success('Valuation Type created successfully');
      }
      setIsDialogOpen(false);
      loadValuationTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save valuation type');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Valuation Types</h1>
            <p className="text-sm text-gray-500 mt-1">Manage valuation type configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Valuation Types</h2>
            {writable && (<Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Valuation Type
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : valuationTypes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No valuation types found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Category Reference</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">External/Internal POs</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {valuationTypes.map((type) => (
                    <tr key={type.valuation_type_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{type.valuation_type_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{type.account_category_reference_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs rounded-full ${type.external_internal_pos_allowed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {type.external_internal_pos_allowed ? 'Allowed' : 'Not Allowed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <RecordActions doctype="valuation_types" onView={() => setViewItem(type)} onEdit={() => handleOpenDialog(type)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingType ? 'Edit Valuation Type' : 'Create Valuation Type'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="valuation_type_id">Valuation Type ID * (10 characters)</Label>
                <Input
                  id="valuation_type_id"
                  name="valuation_type_id"
                  defaultValue={editingType?.valuation_type_id || ''}
                  disabled={editingType !== null}
                  required
                  maxLength={10}
                  minLength={1}
                  pattern="[A-Za-z0-9]{1,10}"
                  title="Must be 1-10 alphanumeric characters"
                  className="mt-1 uppercase"
                  placeholder="e.g., EXTERNAL, INTERNAL"
                  onInput={(e) => {
                    e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">Max 10 alphanumeric characters</p>
              </div>
              <div>
                <Label htmlFor="account_category_reference_id">Account Category Reference *</Label>
                <Select value={selectedAccountCategoryRef} onValueChange={setSelectedAccountCategoryRef} required>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select Account Category Reference" />
                  </SelectTrigger>
                  <SelectContent>
                    {accountCategoryReferences.map((ref) => (
                      <SelectItem key={ref.account_category_reference_id} value={ref.account_category_reference_id}>
                        {ref.account_category_reference_id} - {ref.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="external_internal_pos_allowed"
                  checked={externalInternalPOsAllowed}
                  onChange={(e) => setExternalInternalPOsAllowed(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="external_internal_pos_allowed" className="cursor-pointer">
                  External & Internal POs Allowed
                </Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingType ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ValuationType;
